// AgreementCreateForm — F-1 from spec §6 / frontend-design §4.
// All field rules per spec §1 Agreement and api-design §3 POST /v1/agreements.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useForm,
  type FieldErrors,
  type SubmitHandler,
  type UseFormRegister,
} from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { agreementsApi, companiesApi, peopleApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { usePrincipal } from '@/auth/PrincipalContext';
import { useToast } from '@/components/ToastHost';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import {
  ApiError,
  getDuplicateAgreementId,
} from '@/types/errors';
import type { AgreementCreateBody } from '@/api/endpoints';
import type { AgreementType } from '@/types/domain';
import { Modal } from '@/components/ModalHost';

interface FormShape {
  client_id: string;
  project_name: string;
  sow_id: string;
  amendment: string;
  type: AgreementType | '';
  start_date: string;
  end_date: string;
  total_value: string;
  estimated_value: string;
  description: string;
  owner_person_id: string;
}

const KNOWN_FIELDS = [
  'client_id',
  'project_name',
  'sow_id',
  'amendment',
  'type',
  'start_date',
  'end_date',
  'total_value',
  'estimated_value',
  'description',
  'owner_person_id',
] as const;

export function AgreementCreateForm() {
  const principal = usePrincipal();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const companies = useQuery({
    queryKey: qk.companies({ all: true }),
    queryFn: () => companiesApi.list({ limit: 100 }),
    staleTime: 5 * 60_000,
  });
  const owners = useQuery({
    queryKey: qk.people({ role: ['admin', 'practice_lead'] }),
    queryFn: () => peopleApi.list({ role: ['admin', 'practice_lead'], limit: 100 }),
    staleTime: 60_000,
  });

  const defaultOwner = useMemo(() => {
    if (!principal) return '';
    if (principal.role === 'practice_lead') return principal.person_id;
    return '';
  }, [principal]);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<FormShape>({
    defaultValues: {
      client_id: '',
      project_name: '',
      sow_id: '',
      amendment: '0',
      type: '',
      start_date: '',
      end_date: '',
      total_value: '',
      estimated_value: '',
      description: '',
      owner_person_id: defaultOwner,
    },
  });

  useEffect(() => {
    if (defaultOwner && !watch('owner_person_id')) {
      setValue('owner_person_id', defaultOwner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOwner]);

  const type = watch('type');
  const isFP = type === 'fixed_price';
  const isTM = type === 't_and_m';

  const createMutation = useMutation({
    mutationFn: (body: AgreementCreateBody) => agreementsApi.create(body),
  });

  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const projectNameRef = useRef<HTMLInputElement | null>(null);

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    setFormMessage(null);
    setDuplicateId(null);
    const body: AgreementCreateBody = {
      client_id: values.client_id,
      project_name: values.project_name.trim(),
      sow_id: values.sow_id.trim(),
      amendment: Number(values.amendment) || 0,
      type: values.type as AgreementType,
      start_date: values.start_date,
      ...(values.end_date ? { end_date: values.end_date } : { end_date: null }),
      ...(isFP ? { total_value: values.total_value } : { total_value: null }),
      ...(isTM && values.estimated_value
        ? { estimated_value: values.estimated_value }
        : { estimated_value: null }),
      ...(values.description ? { description: values.description } : { description: null }),
      ...(values.owner_person_id ? { owner_person_id: values.owner_person_id } : {}),
    };
    try {
      const created = await createMutation.mutateAsync(body);
      void qc.invalidateQueries({ queryKey: ['agreements'] });
      toast.show({ severity: 'success', message: 'Agreement created.' });
      navigate(`/agreements/${created.id}`);
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN_FIELDS as unknown as string[]);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.blockingModal === 'duplicate_identity' && err instanceof ApiError) {
        setDuplicateId(getDuplicateAgreementId(err));
      }
      setFormMessage(mapped.formMessage);
    }
  };

  // Browser warning when navigating away from a dirty form (V1 safety
  // net per G-FE-1).
  useDirtyFormReloadGuard(Object.keys(dirtyFields).length > 0);

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
    >
      {formMessage ? (
        <div className="inline-error" role="alert">
          {formMessage}
        </div>
      ) : null}

      <div className="form-field">
        <label htmlFor="client_id">
          Client<span className="form-required" aria-hidden="true">*</span>
        </label>
        <select
          id="client_id"
          aria-required="true"
          aria-invalid={errors.client_id ? 'true' : 'false'}
          aria-describedby={errors.client_id ? 'err-client_id' : undefined}
          disabled={isSubmitting || companies.isPending}
          {...register('client_id', { required: 'Select a client.' })}
        >
          <option value="">Select a client…</option>
          {companies.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {errors.client_id ? (
          <span id="err-client_id" className="form-error">
            {errors.client_id.message}
          </span>
        ) : null}
      </div>

      <div className="form-field">
        <label htmlFor="project_name">
          Project name<span className="form-required" aria-hidden="true">*</span>
        </label>
        <ProjectNameInput
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          assignRef={(el) => {
            projectNameRef.current = el;
          }}
        />
        {errors.project_name ? (
          <span className="form-error">{errors.project_name.message}</span>
        ) : null}
      </div>

      <div className="form-field">
        <label htmlFor="sow_id">
          SOW ID<span className="form-required" aria-hidden="true">*</span>
        </label>
        <input
          id="sow_id"
          aria-required="true"
          aria-invalid={errors.sow_id ? 'true' : 'false'}
          disabled={isSubmitting}
          {...register('sow_id', {
            required: 'SOW ID is required.',
            validate: (v) => v.trim().length > 0 || 'SOW ID is required.',
          })}
        />
        {errors.sow_id ? (
          <span className="form-error">{errors.sow_id.message}</span>
        ) : null}
      </div>

      <details>
        <summary>Amendment number (advanced)</summary>
        <div className="form-field" style={{ marginTop: 8 }}>
          <label htmlFor="amendment">Amendment</label>
          <input
            id="amendment"
            type="number"
            min={0}
            step={1}
            disabled={isSubmitting}
            {...register('amendment', {
              validate: (v) => Number(v) >= 0 || 'Amendment must be 0 or greater.',
            })}
          />
          {errors.amendment ? (
            <span className="form-error">{errors.amendment.message}</span>
          ) : null}
        </div>
      </details>

      <fieldset className="form-field">
        <legend>
          Type<span className="form-required" aria-hidden="true">*</span>
        </legend>
        <label style={{ display: 'inline-flex', gap: 6, marginRight: 16 }}>
          <input
            type="radio"
            value="t_and_m"
            disabled={isSubmitting}
            {...register('type', { required: 'Choose a type.' })}
          />{' '}
          T&M
        </label>
        <label style={{ display: 'inline-flex', gap: 6 }}>
          <input
            type="radio"
            value="fixed_price"
            disabled={isSubmitting}
            {...register('type', { required: 'Choose a type.' })}
          />{' '}
          Fixed-Price
        </label>
        {errors.type ? (
          <span className="form-error">{errors.type.message}</span>
        ) : null}
      </fieldset>

      <div className="form-field">
        <label htmlFor="start_date">
          Start date<span className="form-required" aria-hidden="true">*</span>
        </label>
        <input
          id="start_date"
          type="date"
          aria-required="true"
          disabled={isSubmitting}
          {...register('start_date', { required: 'Start date is required.' })}
        />
        {errors.start_date ? (
          <span className="form-error">{errors.start_date.message}</span>
        ) : null}
      </div>

      <div className="form-field">
        <label htmlFor="end_date">End date</label>
        <input
          id="end_date"
          type="date"
          disabled={isSubmitting}
          {...register('end_date', {
            validate: (v) => {
              if (!v) return true;
              const start = watch('start_date');
              if (start && v < start)
                return 'End date must be on or after the start date.';
              return true;
            },
          })}
        />
        {errors.end_date ? (
          <span className="form-error">{errors.end_date.message}</span>
        ) : null}
      </div>

      {isFP ? (
        <div className="form-field">
          <label htmlFor="total_value">
            Total value (USD)
            <span className="form-required" aria-hidden="true">*</span>
          </label>
          <input
            id="total_value"
            type="text"
            inputMode="decimal"
            placeholder="150000.00"
            aria-required="true"
            disabled={isSubmitting}
            {...register('total_value', {
              required: isFP ? 'Total value is required for Fixed-Price.' : false,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents (e.g., 150000.00).',
              },
            })}
          />
          {errors.total_value ? (
            <span className="form-error">{errors.total_value.message}</span>
          ) : null}
        </div>
      ) : null}

      {isTM ? (
        <div className="form-field">
          <label htmlFor="estimated_value">Estimated value (USD)</label>
          <input
            id="estimated_value"
            type="text"
            inputMode="decimal"
            placeholder="50000.00"
            disabled={isSubmitting}
            {...register('estimated_value', {
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents (e.g., 50000.00).',
              },
            })}
          />
          {errors.estimated_value ? (
            <span className="form-error">{errors.estimated_value.message}</span>
          ) : null}
        </div>
      ) : null}

      <div className="form-field">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          rows={4}
          disabled={isSubmitting}
          {...register('description')}
        />
      </div>

      <div className="form-field">
        <label htmlFor="owner_person_id">
          Owner<span className="form-required" aria-hidden="true">*</span>
        </label>
        <select
          id="owner_person_id"
          aria-required="true"
          disabled={isSubmitting || owners.isPending}
          {...register('owner_person_id', { required: 'Choose an owner.' })}
        >
          <option value="">Select an owner…</option>
          {owners.data?.data.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.email})
            </option>
          ))}
        </select>
        {errors.owner_person_id ? (
          <span className="form-error">{errors.owner_person_id.message}</span>
        ) : null}
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || companies.isPending || owners.isPending}
          aria-busy={isSubmitting ? 'true' : 'false'}
        >
          {isSubmitting ? 'Creating…' : 'Create agreement'}
        </button>
      </div>

      <Modal
        open={!!duplicateId || (formMessage?.startsWith('An item') ?? false)}
        onClose={() => {
          setDuplicateId(null);
          setFormMessage(null);
          window.queueMicrotask(() => projectNameRef.current?.focus());
        }}
        title="An agreement with this identity already exists"
        role="alertdialog"
        scrimDismissDisabled
      >
        <p>
          An agreement with this Client, Project Name, SOW ID, and Amendment
          already exists.
        </p>
        <div className="form-actions">
          {duplicateId ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/agreements/${duplicateId}`)}
            >
              View existing agreement
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setDuplicateId(null);
              setFormMessage(null);
              window.queueMicrotask(() => projectNameRef.current?.focus());
            }}
          >
            Edit identity
          </button>
        </div>
      </Modal>
    </form>
  );
}

// Splits the RHF register's ref-callback so we can chain a local
// ref into the same input. Without this helper, supplying both an
// inline ref={...} prop and the spread `{...register('field')}`
// causes ref to be specified twice (TS compile error).
function ProjectNameInput({
  register,
  errors,
  isSubmitting,
  assignRef,
}: {
  register: UseFormRegister<FormShape>;
  errors: FieldErrors<FormShape>;
  isSubmitting: boolean;
  assignRef: (el: HTMLInputElement | null) => void;
}) {
  const reg = register('project_name', {
    required: 'Project name is required.',
    validate: (v) => v.trim().length > 0 || 'Project name is required.',
  });
  return (
    <input
      id="project_name"
      type="text"
      aria-required="true"
      aria-invalid={errors.project_name ? 'true' : 'false'}
      disabled={isSubmitting}
      name={reg.name}
      onChange={reg.onChange}
      onBlur={reg.onBlur}
      ref={(el) => {
        reg.ref(el);
        assignRef(el);
      }}
    />
  );
}
